import type { ContractsBulkResponse } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractsBulk } from './fetchContractsBulk';

/**
 * Generates the query options for the contracts bulk query so they can be shared between the useQuery hook and prefetching.
 *
 * @param contractIds - The IDs of the contracts to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractsBulkQueryOptions = <TData = ContractsBulkResponse>({
  contractIds,
  options,
}: {
  contractIds: UUID[] | undefined;
  options?: Omit<
    UseQueryOptions<ContractsBulkResponse | undefined, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractIds?.length
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractsBulk(contractIds, { signal })
      : skipToken,
    queryKey: queryKeysContract.getBulk(contractIds ?? []),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting multiple contracts by IDs in bulk.
 *
 * @param contractIds - The IDs of the contracts to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contracts_by_ids_contracts_query_ids_post
 */
export const useQueryContractsBulk = (
  contractIds: UUID[] | undefined,
  options?: Omit<
    UseQueryOptions<
      ContractsBulkResponse | undefined,
      Error,
      ContractsBulkResponse | undefined
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContractsBulkQueryOptions<ContractsBulkResponse | undefined>({
      contractIds,
      options,
    }),
  );
